import { Injectable, inject } from "@angular/core";

import { DatabaseService } from "../database/database.service";
import { rosterName } from "./timer-crew";

/** One person from the ARA crew pack (process `crew`, Fleetplan users). */
export interface CrewOption {
  selectionId?: string; // Picker identity for roster contacts without a Fleetplan user id.
  externalId: string; // Fleetplan user id
  givenNames: string;
  familyName: string;
  login: string;
  roles: string[]; // Fleetplan roles, e.g. PIC, TRI
}

export function mergeCrewContacts(
  directory: CrewOption[],
  roster: { crewMemberId: number; crewMemberDesig: string }[],
  descriptions: Map<string, string[]>
): CrewOption[] {
  const result = directory.map((person) => ({ ...person, roles: [...person.roles] }));
  const normalize = (name: string) =>
    name.normalize("NFKC").replace(/,/g, " ").trim().replace(/\s+/g, " ").toLowerCase();
  for (const person of roster) {
    if (!person.crewMemberDesig?.trim()) continue;
    const names = rosterName(person.crewMemberDesig);
    const roles = (descriptions.get(String(person.crewMemberId)) ?? [])
      .flatMap((value) => value.toUpperCase().split(/[,;\s]+/))
      .filter(Boolean);
    const existing = result.find(
      (option) =>
        normalize(option.givenNames + " " + option.familyName) === normalize(person.crewMemberDesig) ||
        normalize(option.familyName + " " + option.givenNames) === normalize(person.crewMemberDesig)
    );
    if (existing) existing.roles = [...new Set([...existing.roles, ...roles])];
    else result.push({ ...names, externalId: "", selectionId: `roster:${person.crewMemberId}`, login: "", roles });
  }
  return result.sort((a, b) => (a.familyName + " " + a.givenNames).localeCompare(b.familyName + " " + b.givenNames));
}

export function crewOptionFromRow(r: Record<string, unknown>): CrewOption {
  const s = (v: unknown) => (v === null || v === undefined ? "" : String(v));
  return {
    externalId: s(r["id"]),
    givenNames: s(r["firstname"]),
    familyName: s(r["lastname"]),
    login: s(r["login"]),
    roles: s(r["roles"]).split(",").filter(Boolean),
  };
}

/**
 * Reads the crew directory for the logbook crew picker. The web client reads it
 * through the app-server. A device or server without the crew pack (OEAMTC, not
 * yet downloaded) gets an empty list, and the form falls back to free-text names.
 */
@Injectable({ providedIn: "root" })
export class CrewDirectoryService {
  private db = inject(DatabaseService);

  async list(): Promise<CrewOption[]> {
    try {
      return (await this.db.namedQuery("crewDirectory")).map(crewOptionFromRow);
    } catch (err) {
      console.warn("logbook: crew directory unavailable", err);
      return [];
    }
  }
}
